// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Toolbar.tsx
 * @input Uses Section, SizeContext, useListFocus, useKeyboardHint, StyleX, spacingVars, sizeVars
 * @output Exports Toolbar component and ToolbarProps
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Toolbar/Toolbar.doc.mjs
 * - /packages/core/src/Toolbar/Toolbar.test.tsx
 * - /packages/core/src/Toolbar/index.ts
 * - /apps/storybook/stories/Toolbar.stories.tsx
 * - /packages/cli/templates/blocks/components/Toolbar/ (showcase blocks)
 */
import { useCallback } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars, sizeVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { Section } from "../Section/Section.js";
import { useListFocus } from "../hooks/useListFocus.js";
import { useKeyboardHint } from "../hooks/useKeyboardHint.js";
import { SizeProvider } from "../SizeContext/SizeContext.js";
import { edgeCompSlot } from "../Layout/edgeCompensation.stylex.js";
import { themeProps } from "../utils/themeProps.js";

/**
 * Map SpacingStep values to spacingVars keys.
 */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const spacingStepToVar = {
  0: '--spacing-0',
  0.5: '--spacing-0-5',
  1: '--spacing-1',
  1.5: '--spacing-1-5',
  2: '--spacing-2',
  3: '--spacing-3',
  4: '--spacing-4',
  5: '--spacing-5',
  6: '--spacing-6',
  8: '--spacing-8',
  10: '--spacing-10'
};
const styles = {
  baseFlex: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    $$css: true
  },
  baseGrid: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x134kloy",
    kGNEyG: "x6s0dn4",
    $$css: true
  },
  vertical: {
    kXwgrk: "xdt5ytf",
    kGNEyG: "x1qjc9v5",
    $$css: true
  },
  startSlot: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    $$css: true
  },
  centerSlot: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    k7Eaqz: "xeuugli",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  endSlot: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x13a6bvl",
    $$css: true
  },
  startOnly: {
    kUk6DE: "x845mor",
    $$css: true
  },
  endOnly: {
    keTefX: "xvc5jky",
    $$css: true
  }
};
const sizeStyles = {
  base: {
    kAzted: "x1gdqauq",
    $$css: true
  }
};

// Dynamic styles for configurable gap and tab indicator offset
const dynamicStyles = {
  gap: gapValue => [{
    kOIVth: gapValue != null ? "x1pidvrl" : gapValue,
    $$css: true
  }, {
    "--x-gap": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(gapValue)
  }],
  tabIndicatorBottom: offset => [{
    "--_tab-indicator-bottom": offset != null ? "xbrl7fg" : offset,
    $$css: true
  }, {
    "--x---_tab-indicator-bottom": offset != null ? offset : undefined
  }]
};

/**
 * Default block padding per toolbar size. Inline padding comes from the
 * parent container (Card, Section, LayoutHeader) via the Section's
 * theme default — the toolbar only controls its vertical tightness.
 */
const defaultBlockPaddingForSize = {
  sm: 2,
  md: 2,
  lg: 2
};
const blockPaddingVarForSize = {
  sm: spacingVars['--spacing-2'],
  md: spacingVars['--spacing-2'],
  lg: spacingVars['--spacing-2']
};

/**
 * Edge compensation inset amount per toolbar size.
 * Equals container-inline-padding minus the toolbar's block padding,
 * creating even spacing around edge-compensated items (ghost buttons, tabs).
 */
const edgeCompInsetForSize = {
  sm: `calc(var(--container-padding-inline-start, ${spacingVars['--spacing-4']}) - ${spacingVars['--spacing-2']})`,
  md: `calc(var(--container-padding-inline-start, ${spacingVars['--spacing-4']}) - ${spacingVars['--spacing-2']})`,
  lg: `calc(var(--container-padding-inline-start, ${spacingVars['--spacing-4']}) - ${spacingVars['--spacing-2']})`
};
/**
 * General-purpose toolbar with start, center, and end content slots.
 *
 * Built on Section, provides flex/grid layout with roving tabindex
 * keyboard navigation via useListFocus. Cascades `size` to child components
 * (Button, TextInput, TabList, Selector) via SizeContext, and applies
 * edge compensation so ghost buttons align flush at container edges.
 *
 * @example
 * ```
 * <Toolbar label="Actions" size="sm"
 *   startContent={<Button label="Cut" variant="ghost" />}
 *   endContent={<Button label="Settings" variant="ghost" />}
 * />
 * ```
 */
export function Toolbar({
  startContent,
  centerContent,
  endContent,
  label,
  size = 'md',
  gap = 1,
  orientation = 'horizontal',
  variant = 'transparent',
  dividers,
  xstyle,
  className,
  style,
  ref,
  onKeyDown: onKeyDownProp,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  role: _role,
  'aria-label': _ariaLabel,
  'aria-orientation': _ariaOrientation,
  ...props
}) {
  const hasCenterContent = centerContent != null;
  const hasStartContent = startContent != null;
  const hasEndContent = endContent != null;
  const gapVar = spacingVars[spacingStepToVar[gap]];
  const {
    listRef,
    handleKeyDown,
    handleFocus
  } = useListFocus({
    itemSelector: 'button, input, [tabindex]',
    orientation,
    hasRovingTabIndex: true,
    hasCaretGuard: true
  });
  const {
    hintElement,
    onKeyDown: onHintKeyDown,
    onFocus: onHintFocus,
    onBlur: onHintBlur
  } = useKeyboardHint({
    orientation
  });
  const handleToolbarKeyDown = useCallback(e => {
    onKeyDownProp?.(e);
    if (e.defaultPrevented) {
      return;
    }
    onHintKeyDown(e);
    handleKeyDown(e);
  }, [onKeyDownProp, onHintKeyDown, handleKeyDown]);
  const handleToolbarFocus = useCallback(e => {
    onFocusProp?.(e);
    if (e.defaultPrevented) {
      return;
    }
    onHintFocus(e);
    handleFocus(e);
  }, [onFocusProp, onHintFocus, handleFocus]);
  const handleToolbarBlur = useCallback(e => {
    onBlurProp?.(e);
    if (e.defaultPrevented) {
      return;
    }
    onHintBlur(e);
  }, [onBlurProp, onHintBlur]);
  return /*#__PURE__*/_jsx(SizeProvider, {
    value: size,
    children: /*#__PURE__*/_jsx(Section, {
      ref: ref,
      variant: variant,
      paddingBlock: defaultBlockPaddingForSize[size],
      dividers: dividers,
      xstyle: xstyle,
      className: className,
      style: style,
      children: /*#__PURE__*/_jsxs("div", {
        ref: listRef,
        role: "toolbar",
        "aria-label": label,
        "aria-orientation": orientation,
        onKeyDown: handleToolbarKeyDown,
        onFocus: handleToolbarFocus,
        onBlur: handleToolbarBlur,
        ...mergeProps(themeProps('toolbar', {
          size
        }), stylex.props(hasCenterContent ? styles.baseGrid : styles.baseFlex, orientation === 'vertical' && styles.vertical, sizeStyles.base, dynamicStyles.gap(gapVar), dynamicStyles.tabIndicatorBottom(`calc(-1 * (${blockPaddingVarForSize[size]}${dividers?.includes('bottom') ? ' + 1px' : ''}))`))),
        ...props,
        children: [hasCenterContent ?
        /*#__PURE__*/
        // Three-slot grid layout
        _jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("div", {
            ...stylex.props(styles.startSlot, edgeCompSlot.inset(edgeCompInsetForSize[size]), dynamicStyles.gap(gapVar)),
            children: startContent
          }), /*#__PURE__*/_jsx("div", {
            ...stylex.props(styles.centerSlot, dynamicStyles.gap(gapVar)),
            children: centerContent
          }), /*#__PURE__*/_jsx("div", {
            ...stylex.props(styles.endSlot, edgeCompSlot.inset(edgeCompInsetForSize[size]), dynamicStyles.gap(gapVar)),
            children: endContent
          })]
        }) :
        /*#__PURE__*/
        // Two-slot flex layout
        _jsxs(_Fragment, {
          children: [hasStartContent && /*#__PURE__*/_jsx("div", {
            ...stylex.props(styles.startSlot, !hasEndContent && styles.startOnly, edgeCompSlot.inset(edgeCompInsetForSize[size]), dynamicStyles.gap(gapVar)),
            children: startContent
          }), hasEndContent && /*#__PURE__*/_jsx("div", {
            ...stylex.props(styles.endSlot, !hasStartContent && styles.endOnly, edgeCompSlot.inset(edgeCompInsetForSize[size]), dynamicStyles.gap(gapVar)),
            children: endContent
          })]
        }), hintElement]
      })
    })
  });
}
Toolbar.displayName = 'Toolbar';